import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import './i18n';
import Header       from './components/Header';
import CategoryNav  from './components/CategoryNav';
import MenuSection  from './components/MenuSection';
import { useMenuData } from './hooks/useMenuData';

// ── Loading Skeleton ───────────────────────────────────────────────────────
function LoadingState() {
  return (
    <div className="px-4 py-6 max-w-2xl mx-auto space-y-6">
      {/* Hero skeleton */}
      <div className="rounded-2xl overflow-hidden border border-bistro-border">
        <div className="skeleton h-52 w-full" />
        <div className="p-4 bg-bistro-card space-y-2">
          <div className="skeleton h-5 w-3/4" />
          <div className="skeleton h-3 w-full" />
          <div className="skeleton h-3 w-2/3" />
        </div>
      </div>
      {/* Normal card skeletons */}
      {[1, 2, 3, 4].map(i => (
        <div key={i} className="flex gap-3 bg-bistro-card rounded-2xl overflow-hidden border border-bistro-border p-3">
          <div className="skeleton w-28 h-20 rounded-lg flex-shrink-0" />
          <div className="flex-1 space-y-2 py-1">
            <div className="skeleton h-4 w-3/4" />
            <div className="skeleton h-3 w-full" />
            <div className="skeleton h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Error State ────────────────────────────────────────────────────────────
function ErrorState({ message, onRetry }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center justify-center min-h-64 px-8 py-12 text-center">
      <span className="text-5xl mb-4">⚠️</span>
      <p className="text-bistro-muted font-sans text-sm mb-4">{message || t('app.error')}</p>
      <button
        onClick={onRetry}
        className="px-6 py-2.5 rounded-xl bg-bistro-gold text-bistro-dark font-sans font-semibold text-sm
                   hover:bg-bistro-amber transition-colors duration-200"
      >
        {t('app.retry')}
      </button>
    </div>
  );
}

// ── Main App ───────────────────────────────────────────────────────────────
export default function App() {
  const { t, i18n }                           = useTranslation();
  const { categories, menuItems, loading, error } = useMenuData();
  const [activeCategory, setActiveCategory]   = useState(null);
  const sectionsRef                           = useRef({});
  const scrollingProgrammatic                 = useRef(false);

  // Set initial active category
  useEffect(() => {
    if (categories.length > 0 && !activeCategory) {
      setActiveCategory(categories[0].id);
    }
  }, [categories, activeCategory]);

  // Current language code (2-char)
  const lang = (i18n.language || 'pl').slice(0, 2);

  // Scroll to section when category nav tapped
  const handleCategorySelect = useCallback((catId) => {
    setActiveCategory(catId);
    const el = sectionsRef.current[catId];
    if (el) {
      scrollingProgrammatic.current = true;
      const yOffset = -110; // header + nav height
      const top = el.getBoundingClientRect().top + window.scrollY + yOffset;
      window.scrollTo({ top, behavior: 'smooth' });
      setTimeout(() => { scrollingProgrammatic.current = false; }, 800);
    }
  }, []);

  // Update active category on scroll (IntersectionObserver)
  useEffect(() => {
    if (categories.length === 0) return;

    const observers = [];
    const options = { rootMargin: '-100px 0px -55% 0px', threshold: 0 };

    categories.forEach(cat => {
      const el = sectionsRef.current[cat.id];
      if (!el) return;
      const obs = new IntersectionObserver(entries => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !scrollingProgrammatic.current) {
            setActiveCategory(cat.id);
          }
        });
      }, options);
      obs.observe(el);
      observers.push(obs);
    });

    return () => observers.forEach(o => o.disconnect());
  }, [categories]);

  return (
    <div
      id="app-root"
      className="min-h-screen bg-bistro-dark"
      style={{
        backgroundImage: `
          radial-gradient(ellipse at 0% 0%, rgba(30,30,30,1) 0%, transparent 70%),
          radial-gradient(ellipse at 100% 100%, rgba(20,20,20,1) 0%, transparent 70%)
        `,
      }}
    >
      {/* ── Fixed top: Header + Category Nav ── */}
      <div className="sticky top-0 z-50">
        <Header />
        {!loading && !error && categories.length > 0 && (
          <CategoryNav
            categories={categories}
            activeCategory={activeCategory}
            onSelect={handleCategorySelect}
            lang={lang}
          />
        )}
      </div>

      {/* ── Main Content ── */}
      <main
        id="menu-content"
        className="max-w-2xl mx-auto pb-safe pb-16"
        style={{ paddingBottom: 'max(4rem, env(safe-area-inset-bottom, 0px))' }}
      >
        {loading && <LoadingState />}
        {error    && <ErrorState message={t('app.error')} onRetry={() => window.location.reload()} />}

        {!loading && !error && categories.map(cat => {
          const catItems = menuItems.filter(i => i.category_id === cat.id);
          return (
            <div
              key={cat.id}
              ref={el => { sectionsRef.current[cat.id] = el; }}
            >
              <MenuSection
                category={cat}
                items={catItems}
                lang={lang}
              />
            </div>
          );
        })}

        {/* Footer */}
        {!loading && !error && (
          <footer className="text-center px-6 pt-4 pb-8">
            <div className="w-16 h-px bg-bistro-gold opacity-20 mx-auto mb-4" />
            <p className="text-bistro-muted font-sans text-xs opacity-60">
              {t('menu.allergy')}
            </p>
            <p className="text-bistro-muted font-sans text-xs opacity-40 mt-2">
              © 2026 Istanbul Bistro, Warszawa
            </p>
          </footer>
        )}
      </main>
    </div>
  );
}
